import { pathOr, path, isNil } from 'ramda'
import { isArray, isString, isFalsy } from 'ramda-adjunct'
import moment from 'moment'
import {
  getDateFromIsoString,
  getTimeFromIsoString,
  toCamelCaseDeep
} from 'src/utils/helpers'

import {
  Notification,
  OfferDetails,
  NotificationGroupSettings,
  Settings,
  InvoiceDetails,
  Cta
} from './records'

const parseNotificationBody = body => {
  switch (true) {
    case isNil(body):
      return null
    case isString(body):
      return body
    case isArray(body):
      return body.map(v => ({ title: v.label, value: v.value }))
    default:
      return null
  }
}

const getNotificationLabel = value => {
  if (isFalsy(value)) {
    return ''
  }

  if (typeof value === 'string') {
    return value
  }

  return value.name || value.company || value.referenceNumber || ''
}

const getNotificationCompanyLabel = value => {
  if (isFalsy(value)) {
    return ''
  }

  if (typeof value === 'string') {
    return value
  }

  return getNotificationLabel(value.company) || value.name || ''
}

const normalizeNotificationOfferDetails = (notification, objectOfferKey) =>
  new OfferDetails({
    referenceNumber: pathOr(
      '',
      ['data', objectOfferKey, 'referenceNumber'],
      notification
    ),
    id: pathOr('', ['data', objectOfferKey, 'id'], notification),
    from: pathOr('', ['data', objectOfferKey, 'details', 'from'], notification),
    fromDate: pathOr(
      '',
      ['data', objectOfferKey, 'details', 'fromDate'],
      notification
    ),
    to: pathOr('', ['data', objectOfferKey, 'details', 'to'], notification),
    toDate: pathOr(
      '',
      ['data', objectOfferKey, 'details', 'toDate'],
      notification
    ),
    loading: pathOr(
      '',
      ['data', objectOfferKey, 'details', 'loading'],
      notification
    ),
    loadingDate: pathOr(
      '',
      ['data', objectOfferKey, 'details', 'loadingDate'],
      notification
    ),
    startDate: pathOr(
      '',
      ['data', objectOfferKey, 'details', 'startDate'],
      notification
    ),
    endDate: pathOr(
      '',
      ['data', objectOfferKey, 'details', 'endDate'],
      notification
    ),
    unloading: pathOr(
      '',
      ['data', objectOfferKey, 'details', 'unloading'],
      notification
    ),
    unloadingDate: pathOr(
      '',
      ['data', objectOfferKey, 'details', 'unloadingDate'],
      notification
    ),
    container: getNotificationLabel(
      pathOr('', ['data', objectOfferKey, 'details', 'container'], notification)
    ),
    cargoType: getNotificationLabel(
      pathOr('', ['data', objectOfferKey, 'details', 'cargoType'], notification)
    ),
    handlerCompany: getNotificationCompanyLabel(
      pathOr(
        '',
        ['data', objectOfferKey, 'details', 'handler', 'company'],
        notification
      )
    ),
    handlerName: getNotificationLabel(
      pathOr(
        '',
        ['data', objectOfferKey, 'details', 'handler', 'name'],
        notification
      )
    ),
    shipowner: getNotificationLabel(
      pathOr('', ['data', objectOfferKey, 'details', 'shipowner'], notification)
    ),
    driver: getNotificationLabel(
      pathOr(
        '',
        ['data', objectOfferKey, 'details', 'driver', 'name'],
        notification
      )
    ),
    driverPhone: pathOr(
      '',
      ['data', objectOfferKey, 'details', 'driver', 'phoneNumber'],
      notification
    )
  })

const normalizeNotificationTransportationOrder = transportationOrder => {
  if (isFalsy(transportationOrder)) {
    return null
  }

  const normalizedOrder = toCamelCaseDeep(transportationOrder)
  const order = {
    ...normalizedOrder,
    ...normalizedOrder.details,
    referenceNumber:
      normalizedOrder.referenceNumber ||
      normalizedOrder.details?.referenceNumber ||
      ''
  }

  return {
    ...order,
    referenceNumber: order.referenceNumber || '',
    container: getNotificationLabel(order.container),
    shipowner: getNotificationLabel(order.shipowner),
    cargoType: getNotificationLabel(order.cargoType),
    loadingUnloadingDate:
      order.loadingUnloadingDate ||
      getDateFromIsoString(order.loadingUnloadingDateTime),
    loadingUnloadingTime:
      order.loadingUnloadingTime ||
      getTimeFromIsoString(order.loadingUnloadingDateTime),
    loadingDate:
      order.loadingDate || getDateFromIsoString(order.loadingDateTime),
    loadingTime:
      order.loadingTime || getTimeFromIsoString(order.loadingDateTime),
    unloadingDate:
      order.unloadingDate || getDateFromIsoString(order.unloadingDateTime),
    unloadingTime:
      order.unloadingTime || getTimeFromIsoString(order.unloadingDateTime),
    forwarder: {
      company: getNotificationCompanyLabel(
        pathOr('', ['forwarder', 'company'], order) ||
          order.forwarderCompany ||
          order.forwarderCompanyName
      ),
      name:
        getNotificationLabel(pathOr('', ['forwarder', 'name'], order)) ||
        order.forwarderName ||
        ''
    }
  }
}

export const normalizeNotificationsList = (arr = []) =>
  arr.map(notification => {
    const notificationData = pathOr({}, ['data'], notification)
    const hasOfferInformation =
      !isFalsy(path(['data', 'offer'], notification)) ||
      !isFalsy(path(['data', 'tracking'], notification))
    const objectOfferKey = !isFalsy(path(['data', 'tracking'], notification))
      ? 'tracking'
      : 'offer'
    const transportationOrder = normalizeNotificationTransportationOrder(
      pathOr(null, ['data', 'transportationOrder'], notification)
    )

    return new Notification({
      ...notification,
      cta: Cta({ ...pathOr({}, ['cta'], notificationData) }),
      transportationOrder,
      subject: pathOr('', ['subject'], notificationData),
      desktopNotification: pathOr(null, ['desktopNotification'], notification),
      code: pathOr(null, ['code'], notificationData),
      type: pathOr(null, ['type'], notification),
      readAt: pathOr(null, ['readAt'], notification),
      invoice: new InvoiceDetails({
        ...pathOr({}, ['invoice'], notificationData),
        ...pathOr({}, ['correctiveNote'], notificationData),
        ...pathOr({}, ['correctiveInvoice'], notificationData),
        transportationOrderNumber: pathOr(
          null,
          ['data', 'transportationOrder', 'referenceNumber'],
          notification
        )
      }),
      marked: moment(pathOr('', ['createdAt'], notification)).isSame(
        moment(),
        'day'
      ),
      createdAt: moment(pathOr('', ['createdAt'], notification)).calendar(
        null,
        {
          sameDay: 'HH:mm',
          nextDay: 'HH:mm',
          lastDay: 'DD.MM HH:mm',
          lastWeek: 'DD.MM HH:mm',
          sameElse: 'DD.MM HH:mm'
        }
      ),
      offer: !hasOfferInformation
        ? null
        : normalizeNotificationOfferDetails(notification, objectOfferKey),
      coworkerName: pathOr('', ['coworker', 'name'], notificationData),
      body: parseNotificationBody(pathOr({}, ['body'], notificationData))
    })
  })

export const normalizeNotificationsSettingsList = arr =>
  arr.map(
    group =>
      new NotificationGroupSettings({
        ...group,
        values: group.values.map(value => new Settings({ ...value }))
      })
  )
